import * as React from "react";
import { AiQuotaService, type AIQuotaUserRow } from "../../../../../services/ai-quota-service";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { extractBackendError } from "../ai-chatbot-utils";

// ── Per-member limit override editor (KOA-18066) ────────────────────────────
// Three modes: follow the workspace default, custom limits (replace the
// default wholesale — a disabled switch means NO cap for that window), or
// fully unlimited. Saving/removing delegates to the parent's refetch so the
// table's server-computed statuses stay authoritative.

type OverrideMode = "default" | "custom" | "unlimited";

interface TokenLimitOverrideModalProps {
  user: AIQuotaUserRow;
  onClose: () => void;
  /** Called after a successful save/remove — parent refetches and closes. */
  onSaved: () => void | Promise<void>;
}

interface State {
  mode: OverrideMode;
  weeklyEnabled: boolean;
  weeklyValue: number;
  monthlyEnabled: boolean;
  monthlyValue: number;
  saving: boolean;
  error: string | null;
}

/** The mode tabs in their on-screen order, so a mode's pane slides the way the selection moved. */
const MODE_ORDER: OverrideMode[] = ["default", "custom", "unlimited"];

const SUGGESTED_WEEKLY_LIMIT = 5000;
const SUGGESTED_MONTHLY_LIMIT = 20000;

function initialStateFor(user: AIQuotaUserRow): State {
  let mode: OverrideMode = "default";
  if (user.override?.isUnlimited) mode = "unlimited";
  else if (user.override) mode = "custom";
  return {
    mode,
    weeklyEnabled: user.override?.weeklyLimitCredits != null,
    weeklyValue: user.override?.weeklyLimitCredits ?? user.effectiveWeeklyLimitCredits ?? SUGGESTED_WEEKLY_LIMIT,
    monthlyEnabled: user.override?.monthlyLimitCredits != null,
    monthlyValue: user.override?.monthlyLimitCredits ?? user.effectiveMonthlyLimitCredits ?? SUGGESTED_MONTHLY_LIMIT,
    saving: false,
    error: null
  };
}

class TokenLimitOverrideModal extends React.PureComponent<TokenLimitOverrideModalProps, State> {
  state: State = initialStateFor(this.props.user);

  private handleSave = async (): Promise<void> => {
    const { user } = this.props;
    const { mode, weeklyEnabled, weeklyValue, monthlyEnabled, monthlyValue } = this.state;
    this.setState({ saving: true, error: null });
    try {
      if (mode === "default") {
        // Only delete when an override actually exists — otherwise it's a no-op.
        if (user.override) {
          await AiQuotaService.DeleteUserQuotaOverride(user.userId);
        }
      } else {
        await AiQuotaService.UpsertUserQuotaOverride(user.userId, {
          isUnlimited: mode === "unlimited",
          weeklyLimitCredits: mode === "custom" && weeklyEnabled ? Math.max(1, Math.round(weeklyValue)) : null,
          monthlyLimitCredits: mode === "custom" && monthlyEnabled ? Math.max(1, Math.round(monthlyValue)) : null
        });
      }
      await this.props.onSaved();
    } catch (err: unknown) {
      this.setState({ saving: false, error: extractBackendError(err) });
    }
  };

  private handleRemoveOverride = async (): Promise<void> => {
    this.setState({ saving: true, error: null });
    try {
      await AiQuotaService.DeleteUserQuotaOverride(this.props.user.userId);
      await this.props.onSaved();
    } catch (err: unknown) {
      this.setState({ saving: false, error: extractBackendError(err) });
    }
  };

  private handleLimitValueChange(field: "weeklyValue" | "monthlyValue", value: string | number): void {
    const parsed: number = typeof value === "number" ? value : parseInt(String(value), 10);
    if (Number.isNaN(parsed)) return;
    this.setState({ [field]: Math.max(1, Math.round(parsed)) } as Pick<State, "weeklyValue">);
  }

  private renderCustomRow(
    label: string,
    unit: string,
    enabled: boolean,
    value: number,
    onToggle: (checked: boolean) => void,
    onValue: (value: string | number) => void
  ) {
    return (
      <div className="aichat-limits__row">
        <div className="aichat-limits__row-label">{label}</div>
        <div className="aichat-limits__row-controls">
          {enabled && (
            <div className="aichat-limits__row-input">
              <KuikaInput
                type="number"
                size="md"
                min={1}
                value={String(value)}
                onChange={(_: React.ChangeEvent<HTMLInputElement>, next: string | number) => onValue(next)}
              />
              <span className="aichat-limits__unit">credits / {unit}</span>
            </div>
          )}
          <KuikaSwitch label={enabled ? "Limited" : "No limit"} checked={enabled} onChange={onToggle} />
        </div>
      </div>
    );
  }

  render() {
    const { user, onClose } = this.props;
    const { mode, weeklyEnabled, weeklyValue, monthlyEnabled, monthlyValue, saving, error } = this.state;
    const name: string = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email;

    return (
      <KModal
        showModal
        header={`Usage limit — ${name}`}
        width={520}
        centered
        dontRenderSpinner
        closeModal={onClose}
        footer={
          <KModalFooter
            onDelete={user.override ? this.handleRemoveOverride : undefined}
            deleteLabel="Remove override"
            onCancel={onClose}
            submitLabel="Save"
            submitTestId="aiChatbotOverrideSave"
            onSubmit={this.handleSave}
            loading={saving}
          />
        }
      >
        <div className="aichat-limits__override" data-testid="aiChatbotOverrideModal">
          <div className="aichat-limits__override-mode">
            <KuikaTabs
              size="sm"
              options={[
                { value: "default", label: "Workspace default" },
                { value: "custom", label: "Custom limits" },
                { value: "unlimited", label: "Unlimited" }
              ]}
              value={mode}
              onChange={(value: string) => this.setState({ mode: value as OverrideMode })}
            />
          </div>

          <KuikaTabContent activeKey={mode} order={MODE_ORDER}>
            <p key="default" className="aichat-limits__override-hint">
              {name} follows the workspace default limits. Any existing custom override will be removed.
            </p>

            <div key="custom" className="aichat-limits__override-pane">
              <p className="aichat-limits__override-hint">
                Custom limits replace the workspace default for {name}. A window left on "No limit" has no cap.
              </p>
              {this.renderCustomRow(
                "Weekly limit",
                "week",
                weeklyEnabled,
                weeklyValue,
                (checked: boolean) => this.setState({ weeklyEnabled: checked }),
                (value: string | number) => this.handleLimitValueChange("weeklyValue", value)
              )}
              {this.renderCustomRow(
                "Monthly limit",
                "month",
                monthlyEnabled,
                monthlyValue,
                (checked: boolean) => this.setState({ monthlyEnabled: checked }),
                (value: string | number) => this.handleLimitValueChange("monthlyValue", value)
              )}
            </div>

            <p key="unlimited" className="aichat-limits__override-hint">
              {name} will be exempt from every usage limit in this workspace.
            </p>
          </KuikaTabContent>

          {error && <div className="aichat-limits__save-error">{error}</div>}
        </div>
      </KModal>
    );
  }
}

export default TokenLimitOverrideModal;
